import type {
  DebatePersonaEntity,
  DebateQuestionEntity,
  DebateResponseEntity,
  DebateVotePromptEntity,
} from '@platform/timeline';
import { registerSem } from '../registry';

// Debate handlers - working with plain JSON since we don't have protobuf schemas yet

registerSem('debate.persona.started', ({ ev, now, convId }) => {
  const data = (ev.data || {}) as any;
  const entityId = data.persona_id || ev.id;
  if (!entityId) return null;
  return {
    kind: 'add',
    convId,
    entity: {
      id: entityId,
      kind: 'debate_persona',
      timestamp: now,
      props: {
        status: 'active',
        persona_id: data.persona_id,
        name: data.name,
        role: data.role,
        perspective: data.perspective,
        personality_traits: data.personality_traits,
        bio: data.bio,
        is_personification: data.is_personification,
      },
    } as DebatePersonaEntity,
  };
});

registerSem('debate.persona.update', ({ ev, now, convId }) => {
  const data = (ev.data || {}) as any;
  const entityId = data.persona_id || ev.id;
  if (!entityId) return null;
  return {
    kind: 'upsert',
    convId,
    entity: {
      id: entityId,
      kind: 'debate_persona',
      timestamp: now,
      props: {
        status: 'active',
        persona_id: data.persona_id,
        name: data.name,
        role: data.role,
        perspective: data.perspective,
        personality_traits: data.personality_traits,
        bio: data.bio,
        is_personification: data.is_personification,
      },
    } as DebatePersonaEntity,
  };
});

registerSem('debate.persona.completed', ({ ev, now, convId }) => {
  const data = (ev.data || {}) as any;
  const entityId = data.persona_id || ev.id;
  if (!entityId) return null;
  return {
    kind: 'upsert',
    convId,
    entity: {
      id: entityId,
      kind: 'debate_persona',
      timestamp: now,
      props: {
        status: data.success === false ? 'error' : 'completed',
        persona_id: data.persona_id,
        name: data.name,
        role: data.role,
        perspective: data.perspective,
        personality_traits: data.personality_traits,
        bio: data.bio,
        is_personification: data.is_personification,
        success: data.success,
        error: data.error,
      },
    } as DebatePersonaEntity,
  };
});

registerSem('debate.question.started', ({ ev, now, convId }) => {
  const data = (ev.data || {}) as any;
  const entityId = data.question_id || ev.id;
  if (!entityId) return null;
  return {
    kind: 'add',
    convId,
    entity: {
      id: entityId,
      kind: 'debate_question',
      timestamp: now,
      props: {
        status: 'active',
        question_id: data.question_id,
        question: data.question,
        round: data.round,
      },
    } as DebateQuestionEntity,
  };
});

registerSem('debate.question.update', ({ ev, now, convId }) => {
  const data = (ev.data || {}) as any;
  const entityId = data.question_id || ev.id;
  if (!entityId) return null;
  return {
    kind: 'upsert',
    convId,
    entity: {
      id: entityId,
      kind: 'debate_question',
      timestamp: now,
      props: {
        status: 'active',
        question_id: data.question_id,
        question: data.question,
        round: data.round,
      },
    } as DebateQuestionEntity,
  };
});

registerSem('debate.question.completed', ({ ev, now, convId }) => {
  const data = (ev.data || {}) as any;
  const entityId = data.question_id || ev.id;
  if (!entityId) return null;
  return {
    kind: 'upsert',
    convId,
    entity: {
      id: entityId,
      kind: 'debate_question',
      timestamp: now,
      props: {
        status: data.success === false ? 'error' : 'completed',
        question_id: data.question_id,
        question: data.question,
        round: data.round,
        success: data.success,
        error: data.error,
      },
    } as DebateQuestionEntity,
  };
});

registerSem('debate.response.started', ({ ev, now, convId }) => {
  const data = (ev.data || {}) as any;
  const entityId = data.response_id || ev.id;
  if (!entityId) return null;
  return {
    kind: 'add',
    convId,
    entity: {
      id: entityId,
      kind: 'debate_response',
      timestamp: now,
      props: {
        status: 'active',
        response_id: data.response_id,
        persona_id: data.persona_id,
        question_id: data.question_id,
        round_number: data.round_number,
        response: data.response,
        tone: data.tone,
        timestamp: data.timestamp,
      },
    } as DebateResponseEntity,
  };
});

// (delta removed) – updates stream progressively from backend snapshots

registerSem('debate.response.update', ({ ev, now, convId }) => {
  const data = (ev.data || {}) as any;
  const entityId = data.response_id || ev.id;
  if (!entityId) return null;
  return {
    kind: 'upsert',
    convId,
    entity: {
      id: entityId,
      kind: 'debate_response',
      timestamp: now,
      props: {
        status: 'active',
        response_id: data.response_id,
        persona_id: data.persona_id,
        question_id: data.question_id,
        round_number: data.round_number,
        response: data.response,
        tone: data.tone,
        timestamp: data.timestamp,
      },
    } as DebateResponseEntity,
  };
});

registerSem('debate.response.completed', ({ ev, now, convId }) => {
  const data = (ev.data || {}) as any;
  const entityId = data.response_id || ev.id;
  if (!entityId) return null;
  return {
    kind: 'upsert',
    convId,
    entity: {
      id: entityId,
      kind: 'debate_response',
      timestamp: now,
      props: {
        status: data.success === false ? 'error' : 'completed',
        response_id: data.response_id,
        persona_id: data.persona_id,
        question_id: data.question_id,
        round_number: data.round_number,
        response: data.response,
        tone: data.tone,
        timestamp: data.timestamp,
        success: data.success,
        error: data.error,
      },
    } as DebateResponseEntity,
  };
});

registerSem('debate.vote_prompt.started', ({ ev, now, convId }) => {
  const data = (ev.data || {}) as any;
  const entityId = data.vote_prompt_id || ev.id;
  if (!entityId) return null;
  return {
    kind: 'add',
    convId,
    entity: {
      id: entityId,
      kind: 'debate_vote_prompt',
      timestamp: now,
      props: {
        status: 'active',
        vote_prompt_id: data.vote_prompt_id,
        question_id: data.question_id,
        round_number: data.round_number,
        prompt_text: data.prompt_text,
        options: data.options || [],
      },
    } as DebateVotePromptEntity,
  };
});

registerSem('debate.vote_prompt.update', ({ ev, now, convId }) => {
  const data = (ev.data || {}) as any;
  const entityId = data.vote_prompt_id || ev.id;
  if (!entityId) return null;
  return {
    kind: 'upsert',
    convId,
    entity: {
      id: entityId,
      kind: 'debate_vote_prompt',
      timestamp: now,
      props: {
        status: 'active',
        vote_prompt_id: data.vote_prompt_id,
        question_id: data.question_id,
        round_number: data.round_number,
        prompt_text: data.prompt_text,
        options: data.options || [],
      },
    } as DebateVotePromptEntity,
  };
});

registerSem('debate.vote_prompt.completed', ({ ev, now, convId }) => {
  const data = (ev.data || {}) as any;
  const entityId = data.vote_prompt_id || ev.id;
  if (!entityId) return null;
  return {
    kind: 'upsert',
    convId,
    entity: {
      id: entityId,
      kind: 'debate_vote_prompt',
      timestamp: now,
      props: {
        status: data.success === false ? 'error' : 'completed',
        vote_prompt_id: data.vote_prompt_id,
        question_id: data.question_id,
        round_number: data.round_number,
        prompt_text: data.prompt_text,
        options: data.options || [],
        success: data.success,
        error: data.error,
      },
    } as DebateVotePromptEntity,
  };
});
